(() => {
  const ROOT_SELECTOR = '[data-mr-product]';

  const formatMoney = (cents, format) => {
    const value = Number(cents || 0) / 100;
    const moneyFormat = format || '${{amount}}';

    const formatted = new Intl.NumberFormat(
      document.documentElement.lang || 'en-US',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    ).format(value);

    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, formatted)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(value))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatted.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(value));
  };

  const parseJson = (root, selector, fallback = null) => {
    const element = root.querySelector(selector);

    if (!element) return fallback;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error(error);
      return fallback;
    }
  };

  const showMedia = (root, mediaId) => {
    if (!mediaId) return;

    const mediaItems = Array.from(
      root.querySelectorAll('[data-mr-product-media]')
    );

    const mobileSwipeEnabled =
      window.matchMedia('(max-width: 749px)').matches &&
      mediaItems.length > 1;

    let activeMedia = null;

    mediaItems.forEach((media) => {
      const active =
        String(media.dataset.mediaId) === String(mediaId);

      media.hidden = mobileSwipeEnabled ? false : !active;
      media.classList.toggle('is-active', active);

      if (active) {
        activeMedia = media;
      }
    });

    root
      .querySelectorAll('[data-mr-product-thumbnail]')
      .forEach((thumbnail) => {
        const active =
          String(thumbnail.dataset.mediaId) === String(mediaId);

        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute(
          'aria-current',
          active ? 'true' : 'false'
        );
      });

    if (mobileSwipeEnabled && activeMedia) {
      const stage = root.querySelector(
        '.mr-product-gallery__stage'
      );

      window.requestAnimationFrame(() => {
        stage?.scrollTo({
          left: activeMedia.offsetLeft,
          behavior: window.matchMedia(
            '(prefers-reduced-motion: reduce)'
          ).matches
            ? 'auto'
            : 'smooth'
        });
      });
    }
  };

  /*
   * MOUNT RIGI MOBILE PRODUCT SWIPE
   * Native horizontal scrolling with scroll snapping.
   */
  const initializeMobileGallery = (root) => {
    const stage = root.querySelector(
      '.mr-product-gallery__stage'
    );

    const mediaItems = Array.from(
      root.querySelectorAll('[data-mr-product-media]')
    );

    if (!stage || mediaItems.length <= 1) {
      return;
    }

    const mobileQuery = window.matchMedia(
      '(max-width: 749px)'
    );

    const updateIndicators = (activeMedia) => {
      if (!activeMedia) return;

      const mediaId = activeMedia.dataset.mediaId;

      mediaItems.forEach((media) => {
        media.classList.toggle(
          'is-active',
          media === activeMedia
        );
      });

      root
        .querySelectorAll('[data-mr-product-thumbnail]')
        .forEach((thumbnail) => {
          const active =
            String(thumbnail.dataset.mediaId) ===
            String(mediaId);

          thumbnail.classList.toggle('is-active', active);
          thumbnail.setAttribute(
            'aria-current',
            active ? 'true' : 'false'
          );
        });
    };

    const synchronizeLayout = () => {
      const activeMedia =
        mediaItems.find((media) => {
          return media.classList.contains('is-active');
        }) || mediaItems[0];

      if (mobileQuery.matches) {
        mediaItems.forEach((media) => {
          media.hidden = false;
        });

        window.requestAnimationFrame(() => {
          stage.scrollTo({
            left: activeMedia.offsetLeft,
            behavior: 'auto'
          });
        });
      } else {
        mediaItems.forEach((media) => {
          media.hidden = media !== activeMedia;
        });
      }
    };

    /*
     * MOUNT RIGI MOBILE SWIPE FALLBACK
     * Explizite Gestenerkennung für Browser, bei denen
     * das native Scrollen innerhalb des Zoom-Buttons
     * nicht zuverlässig ausgelöst wird.
     */
    let swipeStartX = 0;
    let swipeStartY = 0;
    let swipeStartIndex = 0;

    stage.addEventListener(
      'touchstart',
      (event) => {
        if (
          !mobileQuery.matches ||
          event.touches.length !== 1
        ) {
          return;
        }

        swipeStartX = event.touches[0].clientX;
        swipeStartY = event.touches[0].clientY;

        const activeIndex = mediaItems.findIndex((media) => {
          return media.classList.contains('is-active');
        });

        swipeStartIndex =
          activeIndex >= 0 ? activeIndex : 0;
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      (event) => {
        if (
          !mobileQuery.matches ||
          event.changedTouches.length !== 1
        ) {
          return;
        }

        const endX = event.changedTouches[0].clientX;
        const endY = event.changedTouches[0].clientY;

        const deltaX = endX - swipeStartX;
        const deltaY = endY - swipeStartY;

        const horizontalSwipe =
          Math.abs(deltaX) >= 35 &&
          Math.abs(deltaX) > Math.abs(deltaY) * 1.15;

        if (!horizontalSwipe) {
          return;
        }

        const direction = deltaX < 0 ? 1 : -1;

        const targetIndex = Math.max(
          0,
          Math.min(
            mediaItems.length - 1,
            swipeStartIndex + direction
          )
        );

        if (targetIndex === swipeStartIndex) {
          return;
        }

        const targetMedia = mediaItems[targetIndex];

        showMedia(
          root,
          targetMedia.dataset.mediaId
        );

        const activeThumbnail = root.querySelector(
          `[data-mr-product-thumbnail][data-media-id="${targetMedia.dataset.mediaId}"]`
        );

        activeThumbnail?.scrollIntoView({
          behavior: window.matchMedia(
            '(prefers-reduced-motion: reduce)'
          ).matches
            ? 'auto'
            : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      },
      { passive: true }
    );

    let scrollFrame = null;

    stage.addEventListener(
      'scroll',
      () => {
        if (!mobileQuery.matches || scrollFrame) {
          return;
        }

        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = null;

          const stageCenter =
            stage.scrollLeft + stage.clientWidth / 2;

          let closestMedia = mediaItems[0];
          let closestDistance = Number.POSITIVE_INFINITY;

          mediaItems.forEach((media) => {
            const mediaCenter =
              media.offsetLeft + media.offsetWidth / 2;

            const distance = Math.abs(
              mediaCenter - stageCenter
            );

            if (distance < closestDistance) {
              closestDistance = distance;
              closestMedia = media;
            }
          });

          updateIndicators(closestMedia);
        });
      },
      { passive: true }
    );

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener(
        'change',
        synchronizeLayout
      );
    } else {
      mobileQuery.addListener(synchronizeLayout);
    }

    synchronizeLayout();
  };

  const initializeProduct = (root) => {
    if (!root || root.dataset.mrProductInitialized === 'true') {
      return;
    }

    root.dataset.mrProductInitialized = 'true';

    initializeMobileGallery(root);

    const variants =
      parseJson(root, '[data-mr-product-variants]', []) || [];

    const moneyFormat =
      parseJson(root, '[data-mr-product-money-format]', '${{amount}}');

    const form = root.querySelector('[data-mr-product-form]');
    const variantInput = root.querySelector('[data-mr-product-variant-id]');
    const price = root.querySelector('[data-mr-product-price]');
    const comparePrice = root.querySelector('[data-mr-product-compare-price]');
    const submit = root.querySelector('[data-mr-product-submit]');
    const submitText = root.querySelector('[data-mr-product-submit-text]');
    const errorBox = root.querySelector('[data-mr-product-error]');
    const availability = root.querySelector(
      '[data-mr-product-availability]'
    );

    const selectedOptions = () => {
      return Array.from(
        root.querySelectorAll('[data-mr-product-option]')
      ).map((group) => {
        return group.querySelector(
          '[data-mr-product-option-button].is-selected'
        )?.dataset.optionValue;
      });
    };

    const findVariant = () => {
      const optionGroups = root.querySelectorAll(
        '[data-mr-product-option]'
      );

      /*
       * Produkte mit nur einer Default-Variante haben
       * keine sichtbaren Optionsgruppen.
       */
      if (optionGroups.length === 0) {
        const currentVariantId = String(
          variantInput?.value || ''
        );

        const currentVariant = variants.find((variant) => {
          return String(variant.id) === currentVariantId;
        });

        if (currentVariant) {
          return currentVariant;
        }

        if (variants.length === 1) {
          return variants[0];
        }

        return null;
      }

      const options = selectedOptions();

      return variants.find((variant) => {
        return variant.options.every((value, index) => {
          return value === options[index];
        });
      });
    };

    const updateAvailability = () => {
      const selections = selectedOptions();

      root
        .querySelectorAll('[data-mr-product-option-button]')
        .forEach((button) => {
          const position =
            Number(button.dataset.optionPosition) - 1;

          const candidateOptions = [...selections];
          candidateOptions[position] =
            button.dataset.optionValue;

          const possible = variants.some((variant) => {
            return variant.available &&
              variant.options.every((value, index) => {
                if (index > position) return true;
                return value === candidateOptions[index];
              });
          });

          button.classList.toggle(
            'is-unavailable',
            !possible
          );

          button.setAttribute(
            'aria-disabled',
            possible ? 'false' : 'true'
          );
        });
    };

    const updateVariant = () => {
      const variant = findVariant();

      if (!variant) {
        if (variantInput) variantInput.value = '';

        if (submit) submit.disabled = true;
        if (submitText) submitText.textContent = 'UNAVAILABLE';

        return;
      }

      if (variantInput) {
        variantInput.value = variant.id;
      }

      if (price) {
        price.textContent = formatMoney(
          variant.price,
          moneyFormat
        );
      }

      if (comparePrice) {
        const onSale =
          variant.compare_at_price &&
          variant.compare_at_price > variant.price;

        comparePrice.hidden = !onSale;
        comparePrice.textContent = onSale
          ? formatMoney(variant.compare_at_price, moneyFormat)
          : '';
      }

      if (submit) {
        submit.disabled = !variant.available;
      }

      if (submitText) {
        submitText.textContent = variant.available
          ? 'ADD TO CART'
          : 'SOLD OUT';
      }

      if (availability) {
        availability.textContent = variant.available
          ? 'In stock'
          : 'Out of stock';
      }

      if (variant.featured_media?.id) {
        showMedia(root, variant.featured_media.id);
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      updateAvailability();
    };

    root.addEventListener('click', (event) => {
      const quantityMinus = event.target.closest(
        '[data-mr-quantity-minus]'
      );
      const quantityPlus = event.target.closest(
        '[data-mr-quantity-plus]'
      );

      if (quantityMinus || quantityPlus) {
        const input = root.querySelector(
          '[data-mr-quantity-input]'
        );

        if (input) {
          const current = Math.max(1, Number(input.value) || 1);
          input.value = String(
            quantityPlus ? current + 1 : Math.max(1, current - 1)
          );
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const thumbnail = event.target.closest(
        '[data-mr-product-thumbnail]'
      );

      if (thumbnail) {
        showMedia(root, thumbnail.dataset.mediaId);
        return;
      }

      const optionButton = event.target.closest(
        '[data-mr-product-option-button]'
      );

      if (optionButton) {
        if (
          optionButton.classList.contains('is-unavailable') &&
          optionButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        const group = optionButton.closest(
          '[data-mr-product-option]'
        );

        group
          ?.querySelectorAll('[data-mr-product-option-button]')
          .forEach((button) => {
            const selected = button === optionButton;

            button.classList.toggle('is-selected', selected);
            button.setAttribute(
              'aria-pressed',
              selected ? 'true' : 'false'
            );
          });

        const valueDisplay = group?.querySelector(
          '[data-mr-product-option-value]'
        );

        if (valueDisplay) {
          valueDisplay.textContent =
            optionButton.dataset.optionValue;
        }

        updateVariant();
        return;
      }

      const tab = event.target.closest(
        '[data-mr-product-tab]'
      );

      if (tab) {
        const target = tab.dataset.tabTarget;

        root
          .querySelectorAll('[data-mr-product-tab]')
          .forEach((button) => {
            const active =
              button.dataset.tabTarget === target;

            button.classList.toggle('is-active', active);
            button.setAttribute(
              'aria-selected',
              active ? 'true' : 'false'
            );
          });

        root
          .querySelectorAll('[data-mr-product-tab-panel]')
          .forEach((panel) => {
            panel.hidden =
              panel.dataset.mrProductTabPanel !== target;
          });

        return;
      }

      const zoomButton = event.target.closest(
        '[data-mr-product-zoom]'
      );

      if (zoomButton) {
        const image = zoomButton.querySelector('img');
        const lightbox = root.querySelector(
          '[data-mr-product-lightbox]'
        );
        const content = root.querySelector(
          '[data-mr-product-lightbox-content]'
        );

        if (!image || !lightbox || !content) return;

        const clone = image.cloneNode(true);
        clone.removeAttribute('loading');

        content.replaceChildren(clone);

        if (typeof lightbox.showModal === 'function') {
          lightbox.showModal();
        }

        return;
      }

      if (
        event.target.closest('[data-mr-product-lightbox-close]')
      ) {
        root
          .querySelector('[data-mr-product-lightbox]')
          ?.close();

        return;
      }

      const recommendationButton = event.target.closest(
        '[data-mr-product-recommendation-add]'
      );

      if (recommendationButton) {
        addRecommendation(
          recommendationButton.dataset.variantId,
          recommendationButton
        );
      }
    });

    root
      .querySelector('[data-mr-product-lightbox]')
      ?.addEventListener('click', (event) => {
        if (event.target === event.currentTarget) {
          event.currentTarget.close();
        }
      });

    if (form) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (!variantInput?.value || submit?.disabled) {
          return;
        }

        const originalText =
          submitText?.textContent || 'ADD TO CART';

        if (errorBox) {
          errorBox.hidden = true;
          errorBox.textContent = '';
        }

        if (submit) submit.disabled = true;
        if (submitText) submitText.textContent = 'ADDING…';

        try {
          const shopRoot =
            window.Shopify?.routes?.root || '/';

          const response = await fetch(
            `${shopRoot}cart/add.js`,
            {
              method: 'POST',
              headers: {
                Accept: 'application/json'
              },
              body: new FormData(form)
            }
          );

          const data = await response.json();

          if (!response.ok) {
            throw new Error(
              data.description ||
              data.message ||
              'This product could not be added.'
            );
          }

          document.dispatchEvent(
            new CustomEvent('mr:cart-updated', {
              detail: {
                source: 'product-page',
                item: data
              }
            })
          );
        } catch (error) {
          console.error(error);

          if (errorBox) {
            errorBox.textContent =
              error.message ||
              'This product could not be added.';
            errorBox.hidden = false;
          }
        } finally {
          const variant = findVariant();

          if (submit) {
            submit.disabled = !variant?.available;
          }

          if (submitText) {
            submitText.textContent = variant?.available
              ? originalText
              : 'SOLD OUT';
          }
        }
      });
    }

    updateAvailability();

    const reviewItems = Array.from(
      root.querySelectorAll('[data-mr-hero-review]')
    );

    if (reviewItems.length > 1) {
      const storageKey = `mr-product-review-${root.dataset.sectionId}-${window.location.pathname}`;
      let selectedId = null;

      try {
        selectedId = window.sessionStorage.getItem(storageKey);
      } catch (error) {
        selectedId = null;
      }

      let selected = reviewItems.find(
        (item) => item.dataset.reviewId === selectedId
      );

      if (!selected) {
        selected = reviewItems[
          Math.floor(Math.random() * reviewItems.length)
        ];

        try {
          window.sessionStorage.setItem(
            storageKey,
            selected.dataset.reviewId
          );
        } catch (error) {
          // sessionStorage is optional; the first rendered review remains the fallback.
        }
      }

      reviewItems.forEach((item) => {
        item.hidden = item !== selected;
      });
    }
  };

  const addRecommendation = async (
    variantId,
    button
  ) => {
    if (!variantId || button.disabled) return;

    const originalText = button.textContent;
    const shopRoot =
      window.Shopify?.routes?.root || '/';

    button.disabled = true;
    button.textContent = 'ADDING…';

    try {
      const response = await fetch(
        `${shopRoot}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: 1
              }
            ]
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.description ||
          data.message ||
          'This product could not be added.'
        );
      }

      document.dispatchEvent(
        new CustomEvent('mr:cart-updated', {
          detail: {
            source: 'product-recommendation'
          }
        })
      );
    } catch (error) {
      console.error(error);
      button.disabled = false;
      button.textContent = originalText;
    }
  };

  const initializeAll = () => {
    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initializeProduct);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAll,
      { once: true }
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    initializeAll
  );
})();
